import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  LOGIN_SUCCESS,
  LOAD_DEFAULTS,
  TABLE_OF_TABLES,
  LOAD_MODULES,
} from '../../../store/actions/actionsTypes'
import { Auth } from '../../../components/modules/API/Auth/auth'
import { Defaults } from '../../../components/modules/API/Defaults/defaults'
import { loadDefaults } from '../../../components/modules/requests/loadDefaults'
import { vibrationFunc } from '../../../components/modules/vibrationModule'
import { handleNotifications } from '../../../components/generic/utils/handleNotifications'

const CODE_LENGTH = 6

const getTwoFactorErrorMsg = (err: any): string => {
  const status = err?.response?.status
  const data = err?.response?.data
  if (status === 421) {
    const remaining = Number(data?.error)
    if (Number.isFinite(remaining) && remaining >= 0) {
      return `הקוד שגוי. נותרו ${remaining} נסיונות`
    }
    return 'הקוד שגוי, נסה שוב'
  }
  if (status === 429) return 'הגעת למספר נסיונות מקסימלי. נסה שוב מאוחר יותר'
  if (status === 410) return 'קוד אימות פג תוקף, נא להכניס קוד חדש'
  if (status === 401 || status === 403) return 'קוד שגוי, נסה שוב'
  return data?.message || data?.error || 'משהו השתבש, נסה שוב'
}

export const useTwoFactorVerify = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [code, setCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const tempToken = new URLSearchParams(location.search).get('tempToken')
  const isComplete = code.length === CODE_LENGTH

  const handleVerify = async () => {
    if (!isComplete || isLoading || !tempToken) return
    setIsLoading(true)
    try {
      const res = await Auth.verify2fa(tempToken, code)
      handleNotifications('ברוכים הבאים', { type: 'success' })
      dispatch({ type: LOGIN_SUCCESS, payload: res })
      const modules = await Defaults.getAvailableModules()
      dispatch({ type: LOAD_MODULES, payload: modules })
      await loadDefaults(res.token, LOAD_DEFAULTS, TABLE_OF_TABLES, dispatch)
      navigate('/main', { replace: true })
    } catch (err: any) {
      vibrationFunc('warning')
      const status = err?.response?.status
      const msg = getTwoFactorErrorMsg(err)
      const type: 'warning' | 'error' =
        status === 421 || status === 401 || status === 403 ? 'warning' : 'error'
      handleNotifications(msg, { type })
      if (status === 410) {
        setTimeout(() => navigate('/login', { replace: true }), 1500)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => navigate('/login', { replace: true })

  return {
    code,
    setCode,
    isLoading,
    isComplete,
    tempToken,
    handleVerify,
    handleBack,
  }
}

export type UseTwoFactorVerifyReturn = ReturnType<typeof useTwoFactorVerify>
